import React, { Component, ChangeEvent } from 'react';
import styled from 'styled-components';
import {
  Input,
  Button,
  Grid,
  Typography,
  Tabs,
  Tooltip,
  AnalyticsPage,
  AnalyticsComponent,
} from '@inplayer-org/inplayer-ui';
import { FaMinus, FaPlus } from 'react-icons/fa';
import { BiInfoCircle } from 'react-icons/bi';
import AsyncSelect from 'react-select/async';

// actions
import { CreateCountrySetPayload, UpdateCountrySetPayload } from 'ducks/merchantDucks/restrictions/actions';

// components
import StyledContainer from 'components/StyledContainer';
import BaseModal, { ModalActions } from 'components/Modals/BaseModal';
import Select from 'components/Multiselect';
import Restricted from 'restrictions/roles/components/Restricted';

// types
import { ModalNames } from 'ducks/merchantDucks/modal/types';
import { OptionTypeBase, ValueType } from 'react-select';

// custom multiselect styles
import { customStyles } from 'components/customMultiselectStyles';

// helpers
import { usStateCitiesOptions } from 'pages/MerchantPages/Settings/Auth/GeoRestrictions/helpers/citiesToOptions';
import { snakeCase } from 'lodash';

const { Container, Cell } = Grid;

const StyledTabs = styled<any>(Tabs)`
  div {
    ${({ isReadOnly }: any) => (isReadOnly ? 'cursor: default;' : '')}
  }
`;

interface Props {
  title: string;
  listType: string;
  options: Array<any>;
  type: string;
  createCountrySet: (data: CreateCountrySetPayload) => any;
  merchantId: number;
  id: string;
  global: number;
  updateCountrySet: (data: UpdateCountrySetPayload) => any;
  countries: (OptionTypeBase | undefined)[];
  isReadOnly?: boolean;
  usOptions: Array<any>;
  selectedStates: Array<any>;
  selectedCities: Array<any>;
}
interface State {
  listType: string;
  name: string;
  options: Array<string>;
  selectedTabIndex: number;
  isUsSelected: boolean;
  isAnyUsStateSelected: boolean;
  usStatesSelected: Array<StateType>;
  usCitiesSelected: Array<CityOptionsType>;
  selectedCities: Array<CityOptionsType>;
  isValid: boolean;
  defaultUsCities: Array<any>;
}

type StateType = {
  value: string;
  label: string;
  disabled: boolean;
};

type CityOptionsType = StateType & {
  region: string;
  country: string;
};

type NoOptionsType = {
  inputValue: string;
};

const tabs = [
  { name: 'deny', icon: <FaMinus />, tag: 'deny' },
  { name: 'allow', icon: <FaPlus />, tag: 'allow' },
];

export default class CountrySetModal extends Component<Props, State> {
  multiselect: React.RefObject<Select> = React.createRef();

  usMultiselect: React.RefObject<Select> = React.createRef();

  usCitiesMultiSelect: React.RefObject<Select> = React.createRef();

  static defaultProps = { isReadOnly: false };

  state = {
    listType: 'blacklist',
    name: '',
    options: [],
    selectedTabIndex: 0,
    isUsSelected: false,
    isAnyUsStateSelected: false,
    usStatesSelected: [],
    usCitiesSelected: [],
    selectedCities: [],
    usOptions: [],
    isValid: true,
    defaultUsCities: [],
  };

  componentDidMount() {
    const { title, listType, options, selectedStates, selectedCities } = this.props;
    const defaultUsStateCities = usStateCitiesOptions.slice(0, 10);
    this.setState({
      name: title,
      listType: listType || 'blacklist',
      options,
      isAnyUsStateSelected: selectedStates.length > 0,
      usStatesSelected: selectedStates,
      usCitiesSelected: selectedCities,
      defaultUsCities: defaultUsStateCities,
    });
  }

  componentDidUpdate(prevProps: Props) {
    const { title, listType, options, selectedStates, selectedCities, countries } = this.props;
    const { title: prevTitle, listType: prevListType, options: prevOptions, countries: prevCoutries } = prevProps;

    if (prevTitle !== title || prevListType !== listType || prevOptions !== options) {
      this.setState({
        name: title,
        listType: listType || 'blacklist',
        options,
        selectedTabIndex: listType === 'whitelist' ? 1 : 0,
      });
    }

    if (prevCoutries !== countries) {
      this.setState({
        isUsSelected: !!countries.find((x: any) => x.value === 'US'),
        isAnyUsStateSelected: selectedStates.length > 0,
        usStatesSelected: selectedStates,
        usCitiesSelected: selectedCities,
      });
    }
  }

  filterStateCities = (statesSelected: Array<StateType>, citiesSelected: Array<CityOptionsType>) => {
    const states = statesSelected.map((state: StateType) => state.value);
    const filteredStateCities = citiesSelected?.filter((city: CityOptionsType) => !states.includes(city.region));
    this.setState({
      usCitiesSelected: filteredStateCities,
    });
  };

  handleActive = (isReadOnly: boolean) => (index: number) => {
    if (isReadOnly) {
      return;
    }

    this.setState({
      listType: tabs[index].name === 'deny' ? 'blacklist' : 'whitelist',
      selectedTabIndex: index,
    });
  };

  handleSave = (closeModal: () => void) => () => {
    const { type, createCountrySet, merchantId, id, global, updateCountrySet } = this.props;
    const { name, listType, usStatesSelected, usCitiesSelected } = this.state;
    const { current: multiselectRef } = this.multiselect;
    const { current: usMultiselectRef } = this.usMultiselect;

    if (multiselectRef) {
      let citiesOptions: any = [];

      citiesOptions = usCitiesSelected.map((city: any) => {
        return { name: city.value, country: city.country, region: city.region };
      });

      const countriesValues = multiselectRef.getSelectedValues();
      let usValues = [];
      if (usMultiselectRef) {
        usValues = usMultiselectRef.getSelectedValues();
      }
      const regionsValues = usValues.filter((x: string) => x.indexOf(',') === -1);
      // the regionsMap object is used in order to check if the state is found in the regionsValues
      const regionsMap = {};
      regionsValues.forEach((item) => {
        regionsMap[item] = true;
      });

      if (type === 'new' || type === 'duplicate') {
        createCountrySet({
          merchantId,
          name,
          type: listType,
          global: 0,
          countries: countriesValues,
          regions: regionsValues,
          cities: citiesOptions,
        });
      } else {
        updateCountrySet({
          merchantId,
          countrySetId: id,
          name,
          type: listType,
          global,
          countries: countriesValues,
          regions: regionsValues,
          cities: citiesOptions,
        });
      }

      if ((countriesValues.length > 0 || usStatesSelected.length > 0 || usCitiesSelected.length > 0) && name) {
        closeModal();
      }
    }
  };

  nameChange = (e: ChangeEvent<HTMLInputElement>) => {
    this.setState({
      name: e.target.value,
    });
  };

  onMultiselectChange = (selected: (OptionTypeBase | undefined)[]) => {
    const isUsSelected = !!selected.find((x) => x?.value === 'US');

    if (isUsSelected) {
      this.setState({
        usStatesSelected: [],
        usCitiesSelected: [],
      });
    }
    this.setState({
      isUsSelected,
    });
  };

  onMultiSelectStateChange = (selectedStates: Array<StateType>) => {
    const { usCitiesSelected } = this.state;
    this.filterStateCities(selectedStates, usCitiesSelected);
    this.setState({
      isAnyUsStateSelected: selectedStates.length > 0,
      usStatesSelected: selectedStates,
    });
  };

  onMultiSelectCitiesChange = (selectedCities: ValueType<CityOptionsType>) => {
    this.setState({
      usCitiesSelected: selectedCities as Array<CityOptionsType>,
    });
  };

  promiseOptions = (inputValue: string) =>
    new Promise((resolve) => {
      setTimeout(() => {
        resolve(this.filterCitiesOptions(inputValue));
      }, 0);
    });

  filterCitiesOptions = (inputVal: string) => {
    if (inputVal.length >= 2) {
      return usStateCitiesOptions.filter((i) => i.label.toLowerCase().includes(inputVal.toLowerCase()));
    }
  };

  render() {
    const { type, options, countries, isReadOnly = false, usOptions } = this.props;

    const {
      name,
      selectedTabIndex,
      isUsSelected,
      usStatesSelected,
      defaultUsCities,
      usCitiesSelected,
      isValid,
    } = this.state;

    let subtitle;
    if (type === 'new') {
      subtitle = 'Create a new country set';
    } else if (type === 'edit') {
      subtitle = 'Edit your country set';
    } else {
      subtitle = 'Duplicate the country set';
    }

    const availableCountries =
      tabs[selectedTabIndex].name === 'deny'
        ? 'Assets will be available everywhere except in these countries'
        : 'Assets will be available only in these countries';

    const availableStates =
      tabs[selectedTabIndex].name === 'deny'
        ? 'Assets will be available everywhere except in these states'
        : 'Assets will be available only in these states';

    const availableCities =
      tabs[selectedTabIndex].name === 'deny'
        ? 'Assets will be available everywhere except in these cities'
        : 'Assets will be available only in these cities';
    return (
      <AnalyticsPage tag={tabs[selectedTabIndex].tag} type="tab">
        <BaseModal
          tag={`${snakeCase(subtitle)}_modal`}
          modalName={ModalNames.Country}
          title={subtitle}
          render={({ closeModal }: ModalActions) => (
            <>
              <StyledContainer columns={12} overflow="visible" maxHeight="100%">
                <Cell width={12}>
                  <Input
                    tag="text_country_set_name"
                    type="text"
                    className="name"
                    placeholder="Country set name"
                    value={name || ''}
                    onChange={this.nameChange}
                    disabled={isReadOnly}
                  />
                </Cell>
                <Cell top={3} width={12}>
                  <StyledTabs
                    tabs={tabs}
                    selectedTabIndex={selectedTabIndex}
                    onTabClick={this.handleActive(isReadOnly)}
                    isReadOnly={isReadOnly}
                  />
                </Cell>
                <Cell top={4} width={12}>
                  <Typography variant="p">{availableCountries}</Typography>
                </Cell>
                <Cell top={5} width={12}>
                  <Select
                    tag="dropdown_countries"
                    ref={this.multiselect}
                    options={options}
                    selectedData={countries}
                    name="countrySet"
                    placeholder="Select countries"
                    isReadOnly={isReadOnly}
                    onChange={this.onMultiselectChange}
                  />
                </Cell>
                <>
                  <Cell top={6} width={12}>
                    <>
                      <Typography variant="p">
                        {availableStates}
                        <Tooltip content="Available for US states only">
                          <BiInfoCircle />
                        </Tooltip>
                      </Typography>
                    </>
                  </Cell>
                  <Cell top={7} width={12}>
                    <Select
                      tag="dropdown_states"
                      ref={this.usMultiselect}
                      options={usOptions}
                      selectedData={usStatesSelected}
                      name="countrySet"
                      placeholder="Select states"
                      isReadOnly={isUsSelected}
                      onChange={this.onMultiSelectStateChange}
                    />
                  </Cell>
                  {!isValid && (
                    <Cell top={8} width={12}>
                      <Typography variant="p">You can only select a state or a city from that state.</Typography>
                    </Cell>
                  )}
                </>
                <>
                  <Cell top={8} width={12}>
                    <>
                      <Typography variant="p">
                        {availableCities}
                        <Tooltip
                          content={`Available for US cities only. 
                                Type at least 2 characters in order to show results.`}
                        >
                          <BiInfoCircle />
                        </Tooltip>
                      </Typography>
                    </>
                  </Cell>
                  <Cell top={9} width={12}>
                    <AnalyticsComponent>
                      {({ pages, tracker, merchantId, ip }) => (
                        <AsyncSelect
                          onMenuOpen={() =>
                            tracker.track({
                              event: 'dropdown_change',
                              type: 'dropdown',
                              tag: 'dropdown_cities',
                              pages,
                              merchantId,
                              ip,
                            })
                          }
                          isMulti
                          cacheOptions
                          loadOptions={this.promiseOptions}
                          defaultOptions={defaultUsCities}
                          value={usCitiesSelected}
                          onChange={(values: ValueType<any>) => {
                            tracker.track({
                              event: 'dropdown_change',
                              type: 'dropdown',
                              tag:
                                values?.map((val: any) => `dropdown_cities_${snakeCase(val.label)}`).join(',') ||
                                'dropdown_cities_empty',
                              pages,
                              merchantId,
                              ip,
                            });
                            return this.onMultiSelectCitiesChange(values);
                          }}
                          isDisabled={isUsSelected}
                          placeholder="Select cities"
                          noOptionsMessage={({ inputValue }: NoOptionsType) =>
                            inputValue.length < 2
                              ? 'Type atleast 2 characters'
                              : `There is no city that contains ${inputValue}`
                          }
                          styles={customStyles}
                          // This prop is needed to prevent the select options dropdown to go under the modal
                          menuPortalTarget={document.body}
                        />
                      )}
                    </AnalyticsComponent>
                  </Cell>
                  {!isValid && (
                    <Cell top={8} width={12}>
                      <Typography variant="p">You can only select a state or a city from that state.</Typography>
                    </Cell>
                  )}
                </>
              </StyledContainer>
              <Container columns={3} alignContent="center" justifyContent="center">
                <Restricted isRestricted={isReadOnly}>
                  <Cell middle left={2} top={3}>
                    <Button tag="button_cancel" fullWidth buttonModifiers={['buttonLink']} onClick={closeModal}>
                      Cancel
                    </Button>
                  </Cell>
                  <Cell middle left={3} top={3}>
                    <Button tag="button_save" fullWidth onClick={this.handleSave(closeModal)}>
                      Save
                    </Button>
                  </Cell>
                </Restricted>
              </Container>
            </>
          )}
        />
      </AnalyticsPage>
    );
  }
}
